import { Toggle } from "@base-ui/react/toggle";
import { ToggleGroup } from "@base-ui/react/toggle-group";
import { useState, type FunctionComponent, type ReactNode } from "react";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";

export interface SegmentedOption {
  label: ReactNode;
  value: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedProps {
  options: (string | SegmentedOption)[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Stretch to the container width, items sharing it equally. */
  block?: boolean;
  size?: "small" | "middle" | "large";
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
  "data-testid"?: string;
}

const itemSizeClass = {
  small: "min-h-5 px-[7px] text-sm",
  middle: "min-h-7 px-[11px] text-sm",
  large: "min-h-9 px-[11px] text-base",
} as const;

const toOption = (option: string | SegmentedOption): SegmentedOption =>
  typeof option === "string" ? { label: option, value: option } : option;

/** Single-choice segmented control (antd `Segmented`); one option is always selected. */
export const Segmented: FunctionComponent<SegmentedProps> = ({
  options,
  value,
  defaultValue,
  onChange,
  block,
  size = "middle",
  className,
  ...props
}) => {
  const items = options.map(toOption);
  // Always drive the group: an uncontrolled Base UI group empties when the active item is pressed again.
  const [internal, setInternal] = useState(defaultValue ?? items[0]?.value ?? "");
  const current = value ?? internal;

  return (
    <ToggleGroup
      value={[current]}
      // Pressing the active item would empty the group — keep the current selection instead.
      onValueChange={(next) => {
        if (next[0] === undefined) return;
        setInternal(next[0]);
        onChange?.(next[0]);
      }}
      className={cn(
        "gap-0.5 rounded-md bg-noa-inset p-0.5 text-noa-muted",
        block ? "flex w-full" : "inline-flex",
        className
      )}
      {...props}
    >
      {items.map((item) => (
        <Toggle
          key={item.value}
          value={item.value}
          disabled={item.disabled}
          className={cn(
            "flex cursor-pointer items-center justify-center gap-1.5 rounded transition-colors",
            "hover:bg-noa-hover hover:text-noa-strong",
            "data-[pressed]:bg-noa-elevated data-[pressed]:text-noa-strong data-[pressed]:shadow-sm data-[pressed]:shadow-noa-shadow",
            FOCUS_RING,
            "data-[disabled]:cursor-not-allowed data-[disabled]:text-noa-disabled data-[disabled]:hover:bg-transparent",
            itemSizeClass[size],
            block && "min-w-0 flex-1"
          )}
        >
          {item.icon}
          <span className={cn(block && "truncate")}>{item.label}</span>
        </Toggle>
      ))}
    </ToggleGroup>
  );
};
